import axios from 'axios';

export interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

// Chat Action Types
export type ChatActionType = 'navigate' | 'prepareResponse' | 'analyzeNotice' | 'viewDemand';

export interface ChatAction {
  type: ChatActionType;
  label: string;
  payload: {
    path?: string;
    eprocId?: string;
    noticeId?: string;
    demandId?: string;
    clientName?: string;
  };
}

// Deep Dive Mode Types
export type ChatMode = 'general' | 'deepDive';

export interface DeepDiveContext {
  mode: 'deepDive';
  healthReport: HealthReportData;
  clientId: string;
  clientName: string;
}

// Action Checklist Types
export type ChecklistPriority = 'urgent' | 'high' | 'medium' | 'low';

export interface ChecklistItem {
  id: string;
  task: string;
  priority: ChecklistPriority;
  status: 'pending' | 'completed';
  deepLink?: ChatAction;
}

// Response metadata for checklist and guided questions
export interface ChatResponseMetadata {
  isGuidedQuestion?: boolean;
  riskLevel?: 'high' | 'medium' | 'low';
  checklist?: ChecklistItem[];
}

export interface ChatResponse {
  message: string;
  actions?: ChatAction[];
  metadata?: ChatResponseMetadata;
}

export interface ChatRequest {
  message: string;
  conversationHistory?: ChatMessage[];
  context?: {
    currentPage?: string;
    currentClientId?: string;
    deepDive?: DeepDiveContext;
  };
}

// Health Report Types
export type HealthStatus = 'good' | 'attention' | 'critical';

export interface HealthSection {
  title: string;
  status: HealthStatus;
  items: string[];
  recommendation?: string;
}

export interface HealthReportData {
  client: {
    id: string;
    name: string;
    panId: string;
    businessType: string | null;
    clientType: string | null;
  };
  generatedAt: string;
  overallStatus: HealthStatus;
  overallSummary: string;
  sections: {
    filings: HealthSection;
    demands: HealthSection;
    notices: HealthSection;
    dataSync: HealthSection;
  };
  recommendations: string[];
  tokensUsed: number;
}

// Health Report Response with cache metadata
export interface HealthReportMetadata {
  source: 'cache' | 'generated';
  cachedAt?: string;
  generatedAt: string;
}

export interface HealthReportResponse {
  status: string;
  data: HealthReportData;
  metadata?: HealthReportMetadata;
}

export interface HealthReportOptions {
  force?: boolean;  // true = bypass cache and regenerate
  signal?: AbortSignal;
}

// Portfolio Health Types
export interface ClientHealthSummary {
  clientId: string;
  clientName: string;
  panId: string;
  status: HealthStatus;
  topIssue: string | null;
  demandAmount: number;
  pendingNotices: number;
  overdueNotices: number;
}

export interface PortfolioHealthData {
  generatedAt: string;
  summary: {
    total: number;
    critical: number;
    attention: number;
    good: number;
  };
  topPriorityClients: ClientHealthSummary[];
  allClients: ClientHealthSummary[];
  insights: string[];
}

const axiosInstance = axios.create({
  baseURL: '/api/proxy/',
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosInstance.interceptors.response.use(
  (response) => response.data,
  (error) => {
    return Promise.reject(error);
  },
);

export const finkAssistantService = {
  chat: async (request: ChatRequest): Promise<ChatResponse> => {
    console.log('[Fink] Sending request:', { message: request.message, historyLength: request.conversationHistory?.length || 0 });
    const response = await axiosInstance.post('taxportal/ai-assistant/chat/', request);
    console.log('[Fink] Received response:', response);
    return response as unknown as ChatResponse;
  },

  /**
   * Streaming chat with Fink - text appears progressively
   * Uses Server-Sent Events (SSE) for real-time streaming
   */
  chatStream: async (
    request: ChatRequest,
    onChunk: (text: string) => void,
    onComplete: (response: ChatResponse) => void,
    onError: (error: string) => void
  ): Promise<void> => {
    console.log('[Fink] Starting stream:', { message: request.message, historyLength: request.conversationHistory?.length || 0 });

    try {
      const response = await fetch('/api/proxy/taxportal/ai-assistant/chat/stream/', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(request),
        credentials: 'include', // Include cookies for auth
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Fink] Stream request failed:', response.status, errorText);
        onError(`Failed to connect: ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        onError('Streaming not supported');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (lines ending with \n\n)
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.type === 'chunk') {
                onChunk(data.text);
              } else if (data.type === 'complete') {
                console.log('[Fink] Stream complete:', { messageLength: data.message?.length });
                onComplete(data as ChatResponse);
              } else if (data.type === 'error') {
                console.error('[Fink] Stream error from server:', data.message);
                onError(data.message);
              }
            } catch (parseError) {
              console.error('[Fink] Failed to parse SSE data:', line, parseError);
            }
          }
        }
      }

      console.log('[Fink] Stream ended');
    } catch (error) {
      console.error('[Fink] Stream error:', error);
      onError(error instanceof Error ? error.message : 'Unknown error');
    }
  },

  /**
   * Generate a health report for a specific client
   * @param clientId - The client ID to generate the report for
   * @param options - Optional settings: force (bypass cache), signal (abort)
   */
  getHealthReport: async (clientId: string, options?: HealthReportOptions): Promise<HealthReportResponse> => {
    console.log('[Fink] Requesting health report for client:', clientId, options?.force ? '(force regenerate)' : '');

    const params = new URLSearchParams();
    params.append('cache', 'true');
    if (options?.force) {
      params.append('force', 'true');
    }

    const response = await axiosInstance.get(`taxportal/ai-assistant/health-report/${clientId}?${params.toString()}`, {
      signal: options?.signal,
    });
    console.log('[Fink] Health report received:', response);
    return response as unknown as HealthReportResponse;
  },

  /**
   * Streaming health report generation - progressive updates with status, sections, and complete report
   * Uses Server-Sent Events (SSE) for real-time streaming
   * @param clientId - The client ID to generate the report for
   * @param callbacks - Event callbacks for different streaming events
   * @param options - Optional settings: force (bypass cache and regenerate)
   */
  getHealthReportStream: async (
    clientId: string,
    callbacks: {
      onStatus?: (message: string) => void;
      onClient?: (client: HealthReportData['client']) => void;
      onSummary?: (summary: { overallStatus: HealthStatus; overallSummary: string }) => void;
      onSection?: (section: { name: string; title: string; status: HealthStatus; items: string[]; recommendation?: string }) => void;
      onCached?: (report: HealthReportData) => void;
      onComplete: (report: HealthReportData) => void;
      onError: (error: string) => void;
    },
    options?: { force?: boolean }
  ): Promise<void> => {
    console.log('[Fink] Starting health report stream for client:', clientId, options?.force ? '(force regenerate)' : '');

    try {
      const params = new URLSearchParams();
      if (options?.force) {
        params.append('force', 'true');
      }
      const queryString = params.toString();
      const url = `/api/proxy/taxportal/ai-assistant/health-report/${clientId}/stream${queryString ? `?${queryString}` : ''}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include', // Include cookies for auth
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('[Fink] Health report stream request failed:', response.status, errorText);
        callbacks.onError(`Failed to connect: ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError('Streaming not supported');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });

        // Process complete SSE messages (lines ending with \n\n)
        const lines = buffer.split('\n\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              switch (data.type) {
                case 'status':
                  console.log('[Fink] Status:', data.message);
                  callbacks.onStatus?.(data.message);
                  break;
                case 'client':
                  console.log('[Fink] Client info received');
                  callbacks.onClient?.(data.data as HealthReportData['client']);
                  break;
                case 'summary':
                  console.log('[Fink] Summary received:', data.data.overallStatus);
                  callbacks.onSummary?.(data.data as { overallStatus: HealthStatus; overallSummary: string });
                  break;
                case 'section':
                  console.log('[Fink] Section received:', data.data.name);
                  callbacks.onSection?.(data.data);
                  break;
                case 'cached':
                  console.log('[Fink] Health report served from cache');
                  callbacks.onCached?.(data.data as HealthReportData);
                  break;
                case 'complete':
                  console.log('[Fink] Health report stream complete');
                  callbacks.onComplete(data.data as HealthReportData);
                  break;
                case 'error':
                  console.error('[Fink] Health report stream error from server:', data.message);
                  callbacks.onError(data.message);
                  break;
              }
            } catch (parseError) {
              console.error('[Fink] Failed to parse SSE data:', line, parseError);
            }
          }
        }
      }

      console.log('[Fink] Health report stream ended');
    } catch (error) {
      console.error('[Fink] Health report stream error:', error);
      callbacks.onError(error instanceof Error ? error.message : 'Unknown error');
    }
  },

  /**
   * Get portfolio health summary for all clients
   */
  getPortfolioHealth: async (): Promise<{ status: string; data: PortfolioHealthData }> => {
    console.log('[Fink] Requesting portfolio health');
    const response = await axiosInstance.get('taxportal/ai-assistant/portfolio-health');
    console.log('[Fink] Portfolio health received:', response);
    return response as unknown as { status: string; data: PortfolioHealthData };
  },
};
